// src/components/ui/input.tsx
import * as React from "react"
import { cn } from "../../lib/utils"

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Visual variant of the input */
  variant?: "default" | "ghost" | "outline"
  /** Size of the input */
  size?: "sm" | "md" | "lg"
  /** Additional description text below the input */
  description?: string
  /** Error message to display below the input */
  error?: string
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ 
    className, 
    type, 
    variant = "default", 
    size = "md",
    description,
    error,
    ...props 
  }, ref) => {
    const variantClasses = {
      default: "border-gray-300 bg-white focus:border-emerald-500",
      ghost: "border-transparent bg-gray-50 focus:bg-white",
      outline: "border-gray-400 bg-transparent focus:border-emerald-600"
    }

    const sizeClasses = {
      sm: "h-8 px-3 py-1.5 text-xs",
      md: "h-10 px-3 py-2 text-sm",
      lg: "h-12 px-4 py-3 text-base"
    }

    const hasError = !!error

    return (
      <div className="w-full">
        <input
          type={type}
          className={cn(
            "w-full rounded-lg text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:cursor-not-allowed disabled:opacity-50 transition-colors",
            variantClasses[variant],
            sizeClasses[size],
            hasError && "border-red-500 focus:ring-red-500 focus:border-red-500",
            className
          )}
          ref={ref}
          {...props}
        />
        {description && !error && (
          <p className="mt-1 text-xs text-gray-500">{description}</p>
        )}
        {error && (
          <p className="mt-1 text-xs text-red-600">{error}</p>
        )}
      </div>
    )
  }
)
Input.displayName = "Input"

export { Input }